import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/components/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Send, Loader2, MessageCircle } from 'lucide-react';
import { MESSAGES } from '@/constants/testIds';
import { toast } from 'sonner';
import axios from 'axios';

const Messages = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [connections, setConnections] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConnections();
  }, []);

  useEffect(() => {
    const userId = searchParams.get('user');
    if (userId && connections.length > 0) {
      const conn = connections.find(c => c._id === userId);
      if (conn) {
        setSelectedUser(conn);
        fetchMessages(userId);
      }
    }
  }, [searchParams, connections]);

  useEffect(() => {
    if (selectedUser) {
      const interval = setInterval(() => {
        fetchMessages(selectedUser._id, true);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [selectedUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConnections = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/connections`,
        { withCredentials: true }
      );
      setConnections(data);
    } catch (error) {
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (userId, silent = false) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages/${userId}`,
        { withCredentials: true }
      );
      setMessages(data);
    } catch (error) {
      if (!silent) {
        toast.error('Failed to load messages');
      }
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;

    setSending(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages`,
        {
          receiver_id: selectedUser._id,
          content: newMessage
        },
        { withCredentials: true }
      );
      setNewMessage('');
      fetchMessages(selectedUser._id);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const selectUser = (connection) => {
    setSelectedUser(connection);
    fetchMessages(connection._id);
  };

  return (
    <div className="h-screen bg-[#FDFBF7] flex flex-col paper-texture">
      <div className="border-b border-[#E1D9CD] bg-white/80 backdrop-blur-xl">
        <div className="container mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-[#5C5955] hover:text-[#6B7A4F]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      <div data-testid={MESSAGES.container} className="flex-1 flex overflow-hidden">
        {/* Sidebar — full width on mobile, fixed sidebar on desktop.
            On mobile, hide sidebar when a conversation is open. */}
        <div
          data-testid={MESSAGES.conversationList}
          className={`w-full sm:w-80 sm:flex-shrink-0 border-r border-[#E1D9CD] bg-white/50 backdrop-blur-sm overflow-y-auto ${
            selectedUser ? 'hidden sm:block' : 'block'
          }`}
        >
          <div className="p-6 border-b border-[#E1D9CD]">
            <h2 className="text-2xl font-light text-[#2D2A26]">
              <span className="font-bold">Messages</span>
            </h2>
          </div>
          {loading ? (
            <div className="p-6 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#6B7A4F] mx-auto" />
            </div>
          ) : connections.length === 0 ? (
            <div className="p-6 text-center">
              <MessageCircle className="w-12 h-12 text-[#6B7A4F] mx-auto mb-3 opacity-50" />
              <p className="text-[#5C5955] text-sm">No connections yet</p>
              <Button
                onClick={() => navigate('/discover')}
                className="mt-4 bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full"
              >
                Find Matches
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-[#E1D9CD]">
              {connections.map((connection) => (
                <button
                  key={connection._id}
                  onClick={() => selectUser(connection)}
                  className={`w-full p-4 text-left transition-colors hover:bg-[#E2E6D5]/30 flex items-center gap-3 ${
                    selectedUser?._id === connection._id ? 'bg-[#E2E6D5]/50' : ''
                  }`}
                >
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#E2E6D5] to-[#E8EBDD] flex items-center justify-center flex-shrink-0 border border-[#E2E6D5]">
                    {connection.profile_picture ? (
                      <img
                        src={connection.profile_picture.startsWith('http') ? connection.profile_picture : `${process.env.REACT_APP_BACKEND_URL}${connection.profile_picture.startsWith('/') ? '' : '/'}${connection.profile_picture}`}
                        alt={connection.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <span className="text-sm font-bold text-[#6B7A4F]">
                        {(connection.name || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#2D2A26] truncate">{connection.name}</h3>
                    {connection.favorite_genres && connection.favorite_genres.length > 0 && (
                      <p className="text-xs text-[#5C5955] truncate">
                        {connection.favorite_genres.slice(0, 2).join(', ')}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Chat pane — hide when no selection on mobile, fill on desktop */}
        <div className={`flex-1 flex flex-col bg-[#FDFBF7] ${
          selectedUser ? 'flex' : 'hidden sm:flex'
        }`}>
          {selectedUser ? (
            <>
              <div className="p-4 sm:p-6 border-b border-[#E1D9CD] bg-white/50 backdrop-blur-sm flex items-center gap-3">
                {/* Mobile back button */}
                <button
                  onClick={() => setSelectedUser(null)}
                  aria-label="Back to conversations"
                  data-testid="messages-back-to-list"
                  className="sm:hidden p-2 -ml-2 rounded-full hover:bg-[#E2E6D5]/40"
                >
                  <ArrowLeft className="w-5 h-5 text-[#5C5955]" />
                </button>
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#E2E6D5] to-[#E8EBDD] flex items-center justify-center flex-shrink-0 border border-[#E2E6D5]">
                  {selectedUser.profile_picture ? (
                    <img
                      src={selectedUser.profile_picture.startsWith('http') ? selectedUser.profile_picture : `${process.env.REACT_APP_BACKEND_URL}${selectedUser.profile_picture.startsWith('/') ? '' : '/'}${selectedUser.profile_picture}`}
                      alt={selectedUser.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-sm font-bold text-[#6B7A4F]">
                      {(selectedUser.name || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-lg text-[#2D2A26] truncate">{selectedUser.name}</h3>
                  {selectedUser.bio && (
                    <p className="text-xs text-[#5C5955] truncate">{selectedUser.bio}</p>
                  )}
                </div>
              </div>

              <div
                data-testid={MESSAGES.chatWindow}
                className="flex-1 overflow-y-auto p-6 space-y-4"
              >
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-[#5C5955]">Start a conversation!</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      data-testid={MESSAGES.messageBubble}
                      className={`flex ${
                        message.sender_id === user._id ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div
                        className={`message-bubble ${
                          message.sender_id === user._id
                            ? 'message-sent'
                            : 'message-received'
                        }`}
                      >
                        <p>{message.content}</p>
                        <p className="text-xs opacity-70 mt-1">
                          {new Date(message.created_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <form
                onSubmit={sendMessage}
                className="p-6 border-t border-[#E1D9CD] bg-white/50 backdrop-blur-sm"
              >
                <div className="flex gap-3">
                  <Input
                    data-testid={MESSAGES.messageInput}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    disabled={sending}
                    className="flex-1 bg-white/50 border-[#E1D9CD] focus:border-[#6B7A4F] rounded-full"
                  />
                  <Button
                    data-testid={MESSAGES.sendBtn}
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full px-6"
                  >
                    {sending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 text-[#6B7A4F] mx-auto mb-4 opacity-50" />
                <h3 className="text-xl font-semibold text-[#2D2A26] mb-2">
                  Select a conversation
                </h3>
                <p className="text-[#5C5955]">
                  Choose a connection from the sidebar to start messaging
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
